import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

/**
 * useScrollReveal observes all elements with `.reveal-on-scroll`, `.reveal-fade`, `.reveal-scale`
 * and applies `.is-visible` when they enter the viewport.
 * Automatically catches dynamically loaded items (CMS data, images) via MutationObserver.
 */
export default function useScrollReveal(dependencies = []) {
  const location = useLocation();

  useEffect(() => {
    if (typeof window === 'undefined') return;

    // If browser doesn't support IntersectionObserver, make all visible immediately
    if (!('IntersectionObserver' in window)) {
      document.querySelectorAll('.reveal-on-scroll, .reveal-fade, .reveal-scale').forEach((el) => {
        el.classList.add('is-visible');
      });
      return;
    }

    const observedSet = new WeakSet();

    const checkElementVisibility = (el) => {
      const rect = el.getBoundingClientRect();
      // If element is already in viewport or just above/below, show it immediately
      if (rect.top < window.innerHeight + 100 && rect.bottom >= -50) {
        el.classList.add('is-visible');
        return true;
      }
      return false;
    };

    const observer = new IntersectionObserver(
      (entries, obs) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting || entry.intersectionRatio > 0) {
            entry.target.classList.add('is-visible');
            obs.unobserve(entry.target);
          }
        });
      },
      {
        threshold: 0.01,
        rootMargin: '0px 0px 80px 0px',
      }
    );

    const observeAll = () => {
      const elements = document.querySelectorAll('.reveal-on-scroll, .reveal-fade, .reveal-scale');
      elements.forEach((el) => {
        if (el.classList.contains('is-visible')) return;
        if (checkElementVisibility(el)) return;
        if (!observedSet.has(el)) {
          observedSet.add(el);
          observer.observe(el);
        }
      });
    };

    // Initial check
    observeAll();

    // Check again after a few short intervals to catch initial animations / render ticks
    const t1 = setTimeout(observeAll, 50);
    const t2 = setTimeout(observeAll, 200);
    const t3 = setTimeout(observeAll, 500);

    // MutationObserver to automatically catch async rendered content (CMS, state updates)
    const mutationObserver = new MutationObserver(() => {
      observeAll();
    });

    mutationObserver.observe(document.body, {
      childList: true,
      subtree: true,
    });

    // Fallback scroll listener
    const onScroll = () => {
      const pendingElements = document.querySelectorAll('.reveal-on-scroll:not(.is-visible), .reveal-fade:not(.is-visible), .reveal-scale:not(.is-visible)');
      pendingElements.forEach((el) => {
        checkElementVisibility(el);
      });
    };

    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll, { passive: true });

    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
      clearTimeout(t3);
      observer.disconnect();
      mutationObserver.disconnect();
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
    };
  }, [location.pathname, ...dependencies]);
}
